'use client';

import { Button, TextPassage } from '@petsmart-ui/sparky';
import { useEffect, useRef, useState } from 'react';
import { useSwiper } from 'swiper/react';
import type { Swiper as SwiperRef } from 'swiper/types';

import { BREAKPOINTS } from '@libs/common-next';
import type { VideoType } from '@libs/common-types';

import { useGalleryStore } from '../../model';

export type VideoSlideProps = Readonly<{
    video: VideoType;
    index: number;
}>;

export const VideoSlide = ({ video, index }: VideoSlideProps) => {
    const [isPlaying, setIsPlaying] = useState(false);

    const ref = useRef<HTMLVideoElement>(null);
    const swiperRef = useSwiper();
    const isModalOpen = useGalleryStore(state => state.isModalOpen);

    useEffect(() => {
        const handler = (swiper: SwiperRef) => {
            if (swiper.activeIndex !== index) {
                ref.current?.pause();
                setIsPlaying(false);
            }
        };

        swiperRef.on('slideChange', handler);

        return () => {
            swiperRef.off('slideChange', handler);
        };
    }, [swiperRef, index]);

    useEffect(() => {
        ref.current?.pause();
        setIsPlaying(false);
    }, [isModalOpen]);

    const playVideo = () => {
        ref.current?.play();
        setIsPlaying(true);
    };

    return (
        <div className="pdp-gallery__video-container">
            {!isPlaying && (
                <Button
                    className="pdp-gallery__play-button"
                    onClick={playVideo}
                    variant="link"
                    iconName="play-main"
                    aria-label="Play product video"
                    iconSize="5xl"
                />
            )}

            <video
                ref={ref}
                className="pdp-gallery__video"
                preload="none"
                poster={video.poster}
                controls={isPlaying}
                onPause={() => {
                    if (!ref.current?.seeking) {
                        setIsPlaying(false);
                    }
                }}
                playsInline
            >
                <source media={`(max-width: ${BREAKPOINTS.sm2}px)`} src={video.sizes.small} type="video/mp4" />
                <source media={`(max-width: ${BREAKPOINTS.lg}px)`} src={video.sizes.medium} type="video/mp4" />
                <source src={video.sizes.large} type="video/mp4" />
                <track kind="captions" />
                Your browser does not support the video tag.
            </video>

            <TextPassage align="center" className="pdp-gallery__video-instruction">
                Click to play video
            </TextPassage>
        </div>
    );
};
